import { Armchair, Wind, Sunrise, Footprints } from "lucide-react";
import { motion } from "framer-motion";
import Reveal from "@/components/Reveal";
import { CONTENT } from "@/content";

const CONSEILS = [
  {
    icon: Armchair,
    num: "Fiche 01",
    title: "La posture au bureau",
    text: "Pieds à plat, écran à hauteur des yeux, pauses toutes les heures : trois réflexes simples pour préserver votre dos au travail.",
  },
  {
    icon: Wind,
    num: "Fiche 02",
    title: "Respirer par le diaphragme",
    text: "Cinq minutes par jour de respiration abdominale suffisent à relâcher les tensions et apaiser le système nerveux.",
  },
  {
    icon: Sunrise,
    num: "Fiche 03",
    title: "Le rituel du matin",
    text: "Quelques étirements doux au réveil relancent la mobilité articulaire et préparent le corps à la journée.",
  },
  {
    icon: Footprints,
    num: "Fiche 04",
    title: "Marcher, tous les jours",
    text: "Vingt à trente minutes de marche quotidienne : le mouvement le plus simple et le plus complet pour votre colonne.",
  },
];

const Conseils = () => (
  <section id="conseils" className="grain bg-sage-pale py-28 md:py-36" data-testid="conseils-section">
    <div className="mx-auto max-w-7xl px-6 lg:px-12">
      <div className="grid grid-cols-1 items-end gap-12 lg:grid-cols-12">
        <Reveal className="max-w-2xl lg:col-span-7">
          <p className="text-xs font-medium uppercase tracking-[0.28em] text-gold">Pédagogie &amp; conseils</p>
          <h2 className="mt-5 font-serif text-4xl font-light leading-tight tracking-tight text-anthracite md:text-5xl">
            Les bons réflexes, <em className="text-sage-dark">entre deux séances</em>
          </h2>
          <p className="mt-6 text-base font-light leading-relaxed text-taupe">
            Comprendre son corps, c'est déjà en prendre soin. Retrouvez ici des
            fiches conseils simples et illustrées, dans l'esprit de l'ebook{" "}
            <em>Corps en Mouvement</em>.
          </p>
          <a
            data-testid="conseils-instagram-button"
            href={CONTENT.instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-shine mt-8 inline-block rounded-md border border-sage px-8 py-4 text-sm font-medium uppercase tracking-[0.14em] text-taupe transition-colors duration-300 hover:bg-ivory"
          >
            Suivre les conseils sur Instagram
          </a>
        </Reveal>

        <Reveal delay={0.15} className="relative lg:col-span-5" data-testid="conseils-mascotte">
          <div className="relative flex justify-center pt-10 lg:justify-end">
            <motion.span
              data-testid="mascotte-bulle"
              aria-hidden="true"
              animate={{ y: [0, -5, 0] }}
              transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
              className="soft-shadow absolute left-0 top-0 z-10 -rotate-3 rounded-lg border border-sage-light/70 bg-ivory px-4 py-2 font-serif text-lg italic text-anthracite"
            >
              Par ici !
            </motion.span>
            <motion.img
              data-testid="mascotte-walk"
              src="/mascotte-tabouret.png"
              alt={CONTENT.images.mascotteAlt}
              loading="lazy"
              animate={{ x: [0, -90, -90, 0, 0] }}
              transition={{
                x: { duration: 15, repeat: Infinity, ease: "easeInOut", times: [0, 0.42, 0.5, 0.92, 1] },
              }}
              className="h-64 w-auto md:h-80 lg:h-[24rem]"
            />
          </div>
        </Reveal>
      </div>

      <div className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {CONSEILS.map((c, i) => (
          <Reveal key={c.title} delay={i * 0.08}>
            <article
              data-testid={`conseil-card-${i}`}
              className="group flex h-full flex-col rounded-xl border border-sage-light/60 bg-ivory/90 p-8 backdrop-blur-sm transition-all duration-500 hover:-translate-y-1 hover:border-gold/40"
            >
              <div className="flex items-center justify-between">
                <c.icon className="h-6 w-6 text-sage-dark" strokeWidth={1} />
                <span className="text-[10px] font-medium uppercase tracking-[0.24em] text-gold">{c.num}</span>
              </div>
              <h3 className="mt-6 font-serif text-2xl font-light leading-snug text-anthracite">{c.title}</h3>
              <p className="mt-3 text-sm font-light leading-relaxed text-taupe">{c.text}</p>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default Conseils;
